// crewbit-admin/src/components/money/VendorSelect.tsx
//
// Wave 7 Step 4 — shared vendor picker for the admin expense panels.
// Replaces the freetext `<input>` for vendor on MoneyExpenseLogPanel +
// MoneyExpenseEditPanel with a `<select>` over the active business's
// vendors plus an inline "Add" button that opens AddVendorPanel.
//
// Contract: parent owns (vendorId, vendorName) and gets both back on
// change. Legacy rows (vendor_name text, vendor_id null) display under
// "Currently:" until the operator picks. After inline create, the new
// vendor is auto-selected once useVendors refetches.

import { useCallback, useState } from 'react';
import type { CSSProperties } from 'react';
import { palette, radii } from '../../styles/tokens';
import { useAuthStore } from '../../stores/authStore';
import { useVendors } from '../../pages/Vendors/hooks';
import { AddVendorPanel } from './AddVendorPanel';
import { Toast } from '../common/Toast';

interface Props {
  vendorId: string | null;
  vendorName: string | null;
  onChange: (vendorId: string | null, vendorName: string | null) => void;
}

const inputStyle: CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  borderRadius: radii.sm,
  border: `1px solid ${palette.g200}`,
  background: palette.white,
  fontFamily: 'inherit',
  fontSize: 13.5,
  color: palette.g800,
  outline: 'none',
  boxSizing: 'border-box',
};

export function VendorSelect({ vendorId, vendorName, onChange }: Props) {
  const businessId = useAuthStore((s) => s.business?.id);
  const { vendors, refetch } = useVendors(businessId);
  const [showAdd, setShowAdd] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  const handleSelect = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === '__add__') {
      setShowAdd(true);
      return;
    }
    if (value === '') {
      onChange(null, null);
      return;
    }
    const picked = vendors.find((v) => v.id === value);
    if (picked) onChange(picked.id, picked.company_name);
  }, [vendors, onChange]);

  const onAddSuccess = useCallback((newVendorId: string) => {
    refetch();
    // Look up the just-created vendor's name from the list (after refetch
    // fires) — until then, optimistically thread the new id with an empty
    // name; the select will show the option once refetch lands.
    onChange(newVendorId, null);
  }, [refetch, onChange]);

  // Legacy row: vendor_name freetext but no vendor_id. Surface as a
  // caption under the select so the operator knows what was there.
  const showLegacy = !vendorId && (vendorName ?? '').length > 0;
  const selectValue = vendorId ?? '';

  return (
    <>
      <select value={selectValue} onChange={handleSelect} style={inputStyle}>
        <option value="">Select vendor…</option>
        {vendors.map((v) => (
          <option key={v.id} value={v.id}>{v.company_name}</option>
        ))}
        <option value="__add__">+ Add new vendor…</option>
      </select>
      {showLegacy ? (
        <div style={{ fontSize: 11, color: palette.g500, marginTop: 4 }}>
          Currently: {vendorName}
        </div>
      ) : null}

      <AddVendorPanel
        isOpen={showAdd}
        onClose={() => setShowAdd(false)}
        onResult={(ok, msg) => setToast({ msg, type: ok ? 'success' : 'error' })}
        onSuccess={onAddSuccess}
      />

      {toast && <Toast message={toast.msg} type={toast.type} isVisible={!!toast} onDismiss={() => setToast(null)} />}
    </>
  );
}

